import { useConfigurationServiceGetCustomApiConfigurationFachhaendlerByFhIdent } from "../../openapi/api/queries";
import {
  validateEmail,
  validatePhoneNumber,
  validateRequired,
} from "../BasicForm/helper";
import { useAppForm } from "../BasicForm/useAppForm";

import { JSX } from "react";
import { Trans, useTranslation } from "react-i18next";

import {
  Box,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { useCsConfiguration } from "@plansoft-configuration-renderer/framework";

export type RequestQuoteFormDefaultValues = {
  salutation: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  country: string;
  postalCode: string;
  city: string;
  street: string;
  number: string;
  contactHours: {
    morning: boolean;
    afternoon: boolean;
  };
  specialistRetailer?: {
    salutation?: string;
    company?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    city?: string;
  };
  comment: string;
  readPrivacyPolicy: boolean;
};

export type RequestQuoteFormProps = {
  defaultValues?: Partial<RequestQuoteFormDefaultValues>;
  onSubmit: (values: RequestQuoteFormDefaultValues) => void;
};

export type specialistRetailerData = {
  id: string;
  attributes: [
    {
      name: string;
      value: string;
    },
  ];
};

export function RequestQuoteForm({
  defaultValues = {},
  onSubmit,
}: RequestQuoteFormProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const validateOptionalEmail = ({
    value,
  }: {
    value: string;
  }): string | undefined => {
    if (!value || value.trim() === "") {
      return undefined;
    }

    return validateEmail({ value, t });
  };

  const context = useCsConfiguration().context;
  const fhIdent = context?.FH_IDENT;

  const { data: specialistRetailerData } =
    useConfigurationServiceGetCustomApiConfigurationFachhaendlerByFhIdent({
      fhIdent: fhIdent,
      lang: i18n.language,
    });

  const specialistRetailerAttributes = (
    specialistRetailerData as specialistRetailerData
  )?.attributes;

  const getSpecialistRetailerAttributeValue = (
    attributeName: string,
  ): string | undefined =>
    specialistRetailerAttributes?.find((attr) => attr.name === attributeName)
      ?.value;

  const specialistRetailerTitle =
    getSpecialistRetailerAttributeValue("ADR_TITEL") ??
    defaultValues.specialistRetailer?.salutation;
  const specialistRetailerCompany =
    getSpecialistRetailerAttributeValue("ADR_FIRMA") ??
    defaultValues.specialistRetailer?.company;
  const specialistRetailerFirstName =
    getSpecialistRetailerAttributeValue("ADR_VORNAME") ??
    defaultValues.specialistRetailer?.firstName;
  const specialistRetailerLastName =
    getSpecialistRetailerAttributeValue("ADR_NAME") ??
    defaultValues.specialistRetailer?.lastName;
  const specialistRetailerEmail =
    getSpecialistRetailerAttributeValue("ADR_MAIL") ??
    defaultValues.specialistRetailer?.email;
  const specialistRetailerCity =
    getSpecialistRetailerAttributeValue("ADR_ORT") ??
    defaultValues.specialistRetailer?.city;

  const isNonEmptyString = (value?: string): boolean => Boolean(value?.trim());

  const form = useAppForm({
    defaultValues: {
      salutation: defaultValues.salutation ?? "",
      firstName: defaultValues.firstName ?? "",
      lastName: defaultValues.lastName ?? "",
      email: defaultValues.email ?? "",
      phone: defaultValues.phone ?? "",
      company: defaultValues.company ?? "",
      country: defaultValues.country ?? "",
      postalCode: defaultValues.postalCode ?? "",
      city: defaultValues.city ?? "",
      street: defaultValues.street ?? "",
      number: defaultValues.number ?? "",
      contactHours: {
        morning: defaultValues.contactHours?.morning ?? false,
        afternoon: defaultValues.contactHours?.afternoon ?? false,
      },
      specialistRetailer: {
        salutation: specialistRetailerTitle ?? "",
        company: specialistRetailerCompany ?? "",
        firstName: specialistRetailerFirstName ?? "",
        lastName: specialistRetailerLastName ?? "",
        email: specialistRetailerEmail ?? "",
        city: specialistRetailerCity ?? "",
      },
      comment: defaultValues.comment ?? "",
      readPrivacyPolicy: defaultValues.readPrivacyPolicy ?? false,
    },
    validators: {},
    onSubmit: async ({ value }) => {
      onSubmit?.(value);
    },
  });

  return (
    <form>
      <DialogContent>
        <Box sx={{ position: "relative" }}>
          <Grid container spacing={2} direction="column">
            <Grid container spacing={2} direction="column">
              <Grid>
                <Typography variant="h6">
                  {t("AS:dialog.requestQuote.title.contactData")}
                </Typography>
              </Grid>
              <Grid size={3}>
                <form.AppField
                  name="salutation"
                  children={(field) => (
                    <field.SelectField
                      options={["male", "female", "none"]}
                      required
                      fullWidth
                    />
                  )}
                  validators={{
                    onBlur: ({ value }) => validateRequired({ value, t }),
                    onSubmit: ({ value }) => validateRequired({ value, t }),
                  }}
                />
              </Grid>
              <Grid container spacing={2}>
                <Grid size={3}>
                  <form.AppField
                    name="firstName"
                    children={(field) => <field.TextField fullWidth required />}
                    validators={{
                      onBlur: ({ value }) => validateRequired({ value, t }),
                      onSubmit: ({ value }) => validateRequired({ value, t }),
                    }}
                  />
                </Grid>
                <Grid size={3}>
                  <form.AppField
                    name="lastName"
                    children={(field) => <field.TextField fullWidth required />}
                    validators={{
                      onBlur: ({ value }) => validateRequired({ value, t }),
                      onSubmit: ({ value }) => validateRequired({ value, t }),
                    }}
                  />
                </Grid>
                <Grid size={3}>
                  <form.AppField
                    name="email"
                    children={(field) => <field.TextField fullWidth required />}
                    validators={{
                      onBlur: ({ value }) => {
                        const requiredError = validateRequired({ value, t });
                        if (requiredError) return requiredError;
                        return validateEmail({ value, t });
                      },
                      onSubmit: ({ value }) => {
                        const requiredError = validateRequired({ value, t });
                        if (requiredError) return requiredError;
                        return validateEmail({ value, t });
                      },
                    }}
                  />
                </Grid>
                <Grid size={3}>
                  <form.AppField
                    name="phone"
                    children={(field) => <field.TextField fullWidth />}
                    validators={{
                      onBlur: ({ value }) => validatePhoneNumber({ value, t }),
                      onSubmit: ({ value }) =>
                        validatePhoneNumber({ value, t }),
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid container spacing={2} direction="column">
              <Grid>
                <Typography variant="h6">
                  {t("AS:dialog.requestQuote.title.companyData")}
                </Typography>
              </Grid>
              <Grid container spacing={2} direction="column">
                <Grid container spacing={2}>
                  <Grid size={3}>
                    <form.AppField
                      name="company"
                      children={(field) => (
                        <field.TextField fullWidth required />
                      )}
                      validators={{
                        onBlur: ({ value }) => validateRequired({ value, t }),
                        onSubmit: ({ value }) => validateRequired({ value, t }),
                      }}
                    />
                  </Grid>
                  <Grid size={3}>
                    <form.AppField
                      name="country"
                      children={(field) => (
                        <field.TextField fullWidth required />
                      )}
                      validators={{
                        onBlur: ({ value }) => validateRequired({ value, t }),
                        onSubmit: ({ value }) => validateRequired({ value, t }),
                      }}
                    />
                  </Grid>
                  <Grid size={3}>
                    <form.AppField
                      name="postalCode"
                      children={(field) => <field.TextField fullWidth />}
                    />
                  </Grid>
                  <Grid size={3}>
                    <form.AppField
                      name="city"
                      children={(field) => <field.TextField fullWidth />}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2} sx={{ width: "50%" }}>
                  <Grid size={6}>
                    <form.AppField
                      name="street"
                      children={(field) => <field.TextField fullWidth />}
                    />
                  </Grid>
                  <Grid size={6}>
                    <form.AppField
                      name="number"
                      children={(field) => <field.TextField fullWidth />}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid container spacing={2} direction="column" pt={2}>
                <Typography variant="h6">
                  {t("AS:dialog.requestQuote.label.specialistRetailer.header")}
                </Typography>

                <Grid container>
                  <Grid size={3}>
                    <form.AppField
                      name="specialistRetailer.salutation"
                      children={(field) => {
                        const isNonEmpty = isNonEmptyString(
                          specialistRetailerTitle,
                        );
                        if (isNonEmpty) {
                          return (
                            <field.TextField fullWidth disabled={isNonEmpty} />
                          );
                        } else {
                          return (
                            <field.SelectField
                              options={["male", "female", "none"]}
                              fullWidth
                            />
                          );
                        }
                      }}
                    />
                  </Grid>
                  <Grid size={3}>
                    <form.AppField
                      name="specialistRetailer.company"
                      children={(field) => (
                        <field.TextField
                          fullWidth
                          disabled={isNonEmptyString(specialistRetailerCompany)}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid size={3}>
                    <form.AppField
                      name="specialistRetailer.firstName"
                      children={(field) => (
                        <field.TextField
                          fullWidth
                          disabled={isNonEmptyString(
                            specialistRetailerFirstName,
                          )}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={3}>
                    <form.AppField
                      name="specialistRetailer.lastName"
                      children={(field) => (
                        <field.TextField
                          fullWidth
                          disabled={isNonEmptyString(
                            specialistRetailerLastName,
                          )}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={3}>
                    <form.AppField
                      name="specialistRetailer.email"
                      children={(field) => (
                        <field.TextField
                          fullWidth
                          disabled={isNonEmptyString(specialistRetailerEmail)}
                        />
                      )}
                      validators={{
                        onBlur: validateOptionalEmail,
                        onSubmit: validateOptionalEmail,
                      }}
                    />
                  </Grid>
                  <Grid size={3}>
                    <form.AppField
                      name="specialistRetailer.city"
                      children={(field) => (
                        <field.TextField
                          fullWidth
                          disabled={isNonEmptyString(specialistRetailerCity)}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid>
                <form.AppField
                  name="contactHours"
                  children={(field) => <field.CheckboxField />}
                />
              </Grid>
            </Grid>

            <Grid>
              <form.AppField
                name="comment"
                children={(field) => <field.TextField fullWidth multiline />}
              />
            </Grid>

            <Grid>
              <form.AppField
                name="readPrivacyPolicy"
                children={(field) => (
                  <FormControlLabel
                    sx={{ pt: 2 }}
                    control={
                      <Checkbox
                        checked={field.state.value}
                        onChange={() => field.setValue(!field.state.value)}
                      />
                    }
                    label={
                      <Typography variant="h6">
                        <Trans
                          i18nKey="AS:dialog.requestQuote.label.readPrivacyPolicy"
                          components={{
                            privacyLink: (
                              <a
                                href={t(
                                  "AS:dialog.requestQuote.url.privacyPolicy",
                                )}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  fontWeight: 800,
                                  color: "#232323",
                                  textDecoration: "none",
                                }}
                              />
                            ),
                            consentLink: (
                              <a
                                href={t(
                                  "AS:dialog.requestQuote.url.consentForm",
                                )}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  fontWeight: 800,
                                  color: "#232323",
                                  textDecoration: "none",
                                }}
                              />
                            ),
                          }}
                        />
                        *
                      </Typography>
                    }
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <form.AppForm>
          <form.FormActionButtons />
        </form.AppForm>
      </DialogActions>
    </form>
  );
}
